<template>
  <div class="w-full flex justify-center pb-16 px-4 sm:px-8 mt-6">
    <div class="w-full max-w-4xl">
      <vs-alert v-if="isMentorView" color="warning" active="true" class="mb-8 shadow-sm rounded-xl">
        <div class="flex items-center text-lg font-medium">
          <i class="feather icon-info mr-2 text-xl"></i>
          <span>Read only mode opened for <b class="ml-1">{{ traineeName }}</b></span>
        </div>
      </vs-alert>
      
      <!-- HEADER / HERO SECTION -->
      <div v-if="currentCompetency" class="mb-8 animate-fade-in-up">
        <div class="bg-white rounded-3xl p-8 sm:p-10 border border-gray-100 shadow-sm flex flex-col sm:flex-row items-center text-center sm:text-left relative overflow-hidden group hover:shadow-md transition-shadow duration-500">
          
          <!-- Icon Wrapper -->
          <div class="flex-shrink-0 mb-6 sm:mb-0 sm:mr-8 relative z-10">
            <span class="inline-flex items-center justify-center w-24 h-24 rounded-full bg-primary/10 shadow-inner group-hover:bg-primary/15 transition-colors duration-500">
              <!-- Safely load image or fallback icon -->
              <img v-if="currentCompetency.icon" :src="currentCompetency.icon" alt="attribute" class="w-12 h-12 transition-transform duration-500 group-hover:scale-110 greyscale-icon" />
              <feather-icon v-else icon="StarIcon" svgClasses="h-10 w-10 text-primary transition-transform duration-500 group-hover:scale-110" />
            </span>
          </div>

          <!-- Title & Status -->
          <div class="relative z-10 flex-grow">
            <div class="flex items-center justify-center sm:justify-start mb-3">
              <h1 class="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight leading-tight mr-3">
                {{ currentCompetency.comp }}
              </h1>
              
              <!-- NEW: INTERACTIVE TOOLTIP NOTIFICATION (Matches image style) -->
              <vs-tooltip 
                v-if="motivationSurvey !== 1" 
                :text="motivationSurvey === 2 ? $t('playerJourney.t3_2') : $t('playerJourney.t3_3')" 
                position="right"
              >
                <span class="inline-block w-5 h-5 bg-yellow-400 border-2 border-white rounded-full shadow-sm cursor-pointer transition-transform hover:scale-110 mt-1"></span>
              </vs-tooltip>
            </div>
            
            <!-- Dynamic Status Badge -->
            <div class="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-bold uppercase tracking-wider shadow-sm transition-colors duration-300"
                 :class="progressStatusProp === 'in_progress' ? 'bg-amber-100 text-amber-700 border border-amber-200' : 'bg-green-100 text-green-700 border border-green-200'">
              
              <!-- Icon for status -->
              <svg v-if="progressStatusProp === 'in_progress'" class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
              <svg v-else class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path></svg>
              
              <span v-if="progressStatusProp === 'in_progress'">{{ $t('progressStatus') }}</span>
              <span v-else>{{ $t('progressStatus1') }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- DEVELOPMENT PROGRESS CHART -->
      <div class="mb-8 animate-fade-in-up" style="animation-delay: 0.1s;">
        <DevelopmentProgress/>
      </div>
      
      <!-- DETAILS ACCORDION & STEPPED SLIDER -->
      <div class="mb-10 animate-fade-in-up" style="animation-delay: 0.2s;">
        <Details :terminology="terminologyProps">
          <SteppedSlider/>
        </Details>
      </div>
      
      <!-- TABS SECTION -->
      <div class="bg-white rounded-3xl shadow-sm border border-gray-100 p-2 sm:p-4 mb-10 overflow-hidden animate-fade-in-up" style="animation-delay: 0.3s;">
        <div class="vs-tabs-div">
          <!-- NEW: Added dynamic class 'has-pending-motivation' -->
          <vs-tabs class="vs-tabs-group custom-visible-tabs modern-tabs" :class="{'has-pending-motivation': motivationSurvey !== 1}" alignment="fixed" color="primary">
            
            <vs-tab :label="$t('skillDetails.sd2')">
              <div class="p-4 sm:p-6">
                  <Feedback 
                    :progressStatus="progressStatusProp" 
                    :terminology="terminologyProps" 
                    @actionStatusChanged="refreshProgress" 
                  />
              </div>
            </vs-tab>
            
            <vs-tab :label="$t('skillDetails.sd3')">
              <div class="p-4 sm:p-6">
                  <Endorsement 
                    :progressStatus="progressStatusProp" 
                    :terminology="terminologyProps" 
                  />
              </div>
            </vs-tab>
            
            <vs-tab :label="$t('skillDetails.sd1')">
              <div class="p-4 sm:p-6">
                  <Progress 
                    :progressStatus="progressStatusProp" 
                    @actionStatusChanged="refreshProgress" 
                  />
              </div>
            </vs-tab>
            
          </vs-tabs>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import Details from "./Details";
import Progress from "./Progress";
import DevelopmentProgress from "./developmentProgress.vue";
import Feedback from "./Feedback";
import Endorsement from "./Endorsement";
import SteppedSlider from "./SteppedSlider";

export default {
  name: "SkillDetails",
  data() {
    return {
      sendEndorsementInvitePopup: true, competency: null, value2: 95, currentCompetency: null,
      progressStatusProp: null, terminologyProps: null, initialLevel: null, trainingDuration: null,
      CCD: this.$t("addNewSkill.data"), progress_measures_list: [], endorsementChartObj: {},
      isMentorView: false, traineeName: "",
      
      // NEW: Added state for motivation dot
      motivationSurvey: 1 // Defaults to 1 (completed/no action)
    };
  },
  components: { Details, Endorsement, Feedback, Progress, SteppedSlider, DevelopmentProgress },
  methods: {
      _searchInDataset(dataset, targetId) {
          if (!dataset || !targetId) return null;
          let parts = targetId.split("-");
          for (let key in dataset) {
              if (dataset[key] && dataset[key].competencies) {
                  let comps = dataset[key].competencies;
                  if (Array.isArray(comps)) {
                      let found = comps.find(c => c.id === targetId);
                      if (found) return found;
                  } else {
                      for (let k in comps) {
                          if (comps[k] && comps[k].id === targetId) return comps[k];
                          if (key === parts[0] && k === parts[1]) return comps[k];
                      }
                  }
              }
          }
          return null;
      },
      getSkillData(targetId) {
          if (!targetId) return null;
          let parts = targetId.split("-");
          if (parts.length >= 3) { targetId = `${parts[0]}-${parts[1]}`; }
          let locale = window.$cookies.get('locale') || (this.$i18n && this.$i18n.locale) || 'en';
          if (locale.includes('-')) { locale = locale.split('-')[0]; }

          let patterns = [];
          if (targetId.startsWith('eg-') || targetId.startsWith('nb-') || targetId.startsWith('ei-') || targetId.startsWith('es-')) { patterns.push(`i18n-${locale}-deep-Data`); } 
          else if (targetId.startsWith('sc-')) { patterns.push(`i18n-${locale}-pci-Data-Coach`, `i18n-${locale}-pci-Data`); } 
          else if (targetId.startsWith('lt-') || targetId.startsWith('l-')) { patterns.push(`i18n-${locale}-Data-DPM`); } 
          else if (targetId.startsWith('pt-')) { patterns.push(`i18n-${locale}-peopletree-Data`); }

          const allPatterns = [ `i18n-${locale}-deep-Data`, `i18n-${locale}-pci-Data-Coach`, `i18n-${locale}-pci-Data`, `i18n-${locale}-peopletree-Data`, `i18n-${locale}-Data-DPM`, `i18n-${locale}-Data` ];
          allPatterns.forEach(p => { if (!patterns.includes(p)) patterns.push(p); });

          if (locale !== 'en') {
              let enPatterns = [];
              if (targetId.startsWith('eg-') || targetId.startsWith('nb-') || targetId.startsWith('ei-') || targetId.startsWith('es-')) { enPatterns.push('i18n-en-deep-Data'); } 
              else if (targetId.startsWith('sc-')) { enPatterns.push('i18n-en-pci-Data-Coach', 'i18n-en-pci-Data'); } 
              else if (targetId.startsWith('lt-') || targetId.startsWith('l-')) { enPatterns.push('i18n-en-Data-DPM'); } 
              else if (targetId.startsWith('pt-')) { enPatterns.push('i18n-en-peopletree-Data'); }

              const allEnPatterns = [ 'i18n-en-deep-Data', 'i18n-en-pci-Data-Coach', 'i18n-en-pci-Data', 'i18n-en-peopletree-Data', 'i18n-en-Data-DPM', 'i18n-en-Data' ];
              allEnPatterns.forEach(p => { if (!enPatterns.includes(p) && !patterns.includes(p)) enPatterns.push(p); });
              patterns = patterns.concat(enPatterns);
          }

          for (let i = 0; i < patterns.length; i++) {
              try {
                  const fallbackModule = require(`@/i18n/${patterns[i]}.js`);
                  const fallbackData = fallbackModule.default || fallbackModule;
                  if (fallbackData && fallbackData.addNewSkill && fallbackData.addNewSkill.data) {
                      let found = this._searchInDataset(fallbackData.addNewSkill.data, targetId);
                      if (found) return found;
                  }
              } catch (err) {}
          }
          return this._searchInDataset(this.CCD, targetId);
      },
      refreshProgress() {
        const id = this.$route.params.traineeCompetenciesId;
        this.$store.dispatch("competencyProgress/fetchCompetencyProgress", {
          query: { traineeCompetenciesId: id, $sort: { createdAt: -1 } }
        }).catch(e => { console.error("Error refreshing progress metrics:", e); });
      }
  },
  created() {
    this.terminologyProps = window.$cookies.get('terminology');
    this.$store.dispatch("endorsementInvite/destroyAllState").catch(e=>{});

    const id = this.$route.params.traineeCompetenciesId;
    
    this.$store.dispatch("trainee-competencies/get", id).then(async (res1) => {
        let currentUserId = null;
        
        if (this.$store.state.AppActiveUser) { currentUserId = this.$store.state.AppActiveUser.usersId || this.$store.state.AppActiveUser.id; }
        if (!currentUserId && localStorage.getItem('userInfo')) {
           let userInfo = JSON.parse(localStorage.getItem('userInfo')); currentUserId = userInfo.usersId || userInfo.id;
        }

        if (res1.usersId && currentUserId && String(res1.usersId) !== String(currentUserId)) {
            this.isMentorView = true;
            if (this.$route.query.traineeName) { this.traineeName = this.$route.query.traineeName; } 
            else {
                this.$store.dispatch('users/find', { query: { id: res1.usersId } }).then(userRes => {
                      if (userRes.data && userRes.data.length > 0) { this.traineeName = userRes.data[0].firstName + ' ' + userRes.data[0].lastName; }
                }).catch(()=>{});
            }
        } else { this.isMentorView = false; }

        if (res1.progressStatus === "in_progress") { this.progressStatus = "In Progress"; this.progressStatusProp = "in_progress"; } 
        else if (res1.progressStatus === "complete") { this.progressStatus = "Goal Completed"; this.progressStatusProp = "complete"; }

        // --- NEW LOGIC: Calculate motivation survey status ---
        this.motivationSurvey = 0;
        if (res1.motivationSurveyDate != null && res1.progressStatus === 'in_progress') {
            if(new Date() < new Date(res1.motivationSurveyDate)) {
                this.motivationSurvey = 1; // Completed / Not Due
            } else {
                this.motivationSurvey = 2; // Overdue / Action Needed
            }
        }
        // -----------------------------------------------------

        let foundCompetency = this.getSkillData(res1.competencyId);

        if (foundCompetency) {
            let pmTitles = [];
            try {
              let pmRes = await this.$store.dispatch("trainee-competencies-prgms/find", { query: { trainee_competencies_id: res1.id } });
              if (pmRes && pmRes.data && foundCompetency.progressMeasures) {
                pmRes.data.forEach(dbPm => {
                  let measureId = dbPm.progress_measure_id || dbPm.progressMeasureId || dbPm.prgms_id || dbPm.id;
                  let matchedPm = foundCompetency.progressMeasures.find(p => p.id === measureId);
                  if (matchedPm && matchedPm.title) { pmTitles.push(matchedPm.title); }
                });
              }
            } catch(err) { console.warn("Could not fetch PRGMS for SkillDetails title formatting"); }

            this.currentCompetency = { ...foundCompetency };
            if (pmTitles.length > 0) { this.currentCompetency.comp = `${foundCompetency.comp} (${pmTitles.join(', ')})`; }
            this.competency = this.currentCompetency.comp;
            
        } else {
            let fallbackName = res1.skill_name || "Custom Skill";
            if (this.$store.state.addSkill && this.$store.state.addSkill.competency && this.$store.state.addSkill.competency.comp) fallbackName = this.$store.state.addSkill.competency.comp;
            this.currentCompetency = { comp: fallbackName, icon: "" };
            this.competency = this.currentCompetency.comp;
        }
      }).catch(e=>{});
  }
};
</script>

<style scoped>
/* Base Layout & Utilities */
.justify-content-center { justify-content: center; }
.items-center { text-align: center; text-align: -moz-center; text-align: -webkit-center; }
.greyscale-icon { mix-blend-mode: multiply; opacity: 0.9; }

/* Custom Animations */
.animate-fade-in-up {
  animation: fadeInUp 0.5s cubic-bezier(0.25, 1, 0.5, 1) forwards;
  opacity: 0;
}

@keyframes fadeInUp {
  0% { opacity: 0; transform: translateY(15px); }
  100% { opacity: 1; transform: translateY(0); }
}

/* Vuesax Tab Overrides for Modern Look */
.custom-visible-tabs /deep/ .vs-tabs--btn { 
  color: #6B7280 !important; 
  font-weight: 600; 
  padding: 1rem 1.5rem;
  font-size: 1rem;
  transition: color 0.3s ease;
}
.custom-visible-tabs /deep/ .vs-tabs--btn:hover {
  color: rgba(var(--vs-primary), 0.8) !important;
}
.custom-visible-tabs /deep/ .activeChild { 
  color: rgba(var(--vs-primary), 1) !important; 
}
.custom-visible-tabs /deep/ .vs-tabs--line {
  height: 3px;
  border-radius: 3px 3px 0 0;
  box-shadow: 0 -2px 10px rgba(var(--vs-primary), 0.3);
}

.modern-tabs /deep/ .vs-tabs--ul {
  box-shadow: none !important;
  border-bottom: 1px solid #F3F4F6;
  background: transparent !important;
}

.has-pending-motivation /deep/ .vs-tabs--ul .vs-tabs--li:last-child .vs-tabs--btn {
    position: relative;
}

.has-pending-motivation /deep/ .vs-tabs--ul .vs-tabs--li:last-child .vs-tabs--btn::after {
    content: '';
    position: absolute;
    top: 10px;    /* Lowered slightly to align better with text */
    right: 150px;  /* Increased from 8px to move it left, closer to the text */
    width: 10px;
    height: 10px;
    background-color: #FBBF15;
    border-radius: 50%;
    border: 2px solid #fff;
    box-shadow: 0 2px 4px rgba(0,0,0,0.15);
    animation: pulse-yellow 2s infinite;
}

@keyframes pulse-yellow {
    0% { box-shadow: 0 0 0 0 rgba(251, 191, 21, 0.4); }
    70% { box-shadow: 0 0 0 6px rgba(251, 191, 21, 0); }
    100% { box-shadow: 0 0 0 0 rgba(251, 191, 21, 0); }
}
</style>